import React, { useState, useEffect, useCallback } from 'react';
import { Shield, AlertTriangle, Activity, Loader2, RotateCw, ExternalLink } from 'lucide-react';
import apiClient from '../../../api/client';
import { buildHubUrl } from '../../../config/externalUrls';

// ── Types ────────────────────────────────────────────────────────────────────

interface AdmetValue {
  value?: number | boolean;
  unit?: string;
  classification: 'green' | 'amber' | 'red';
  confidence?: number;
  note?: string;
  score?: number;
  risk?: string;
}

interface CypIsoform {
  inhibition_probability: number;
  risk: string;
  classification: 'green' | 'amber' | 'red';
  confidence: number;
}

interface AdmetResult {
  smiles: string;
  descriptors: Record<string, number>;
  absorption: Record<string, AdmetValue>;
  distribution: Record<string, AdmetValue>;
  metabolism: {
    cyp_inhibition: Record<string, CypIsoform>;
    metabolic_stability: AdmetValue;
  };
  excretion: Record<string, AdmetValue>;
  toxicity: Record<string, AdmetValue>;
}

interface ToxAlert {
  alert_name: string;
  alert_type: string;
  matched_smarts?: string;
  description?: string;
}

interface ToxResult {
  smiles: string;
  total_alerts: number;
  overall_risk: string;
  alerts: ToxAlert[];
}

interface ADMETTabProps {
  smiles: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const DOT_COLORS: Record<string, string> = {
  green: '#10b981',
  amber: '#f59e0b',
  red: '#ef4444',
};

const TrafficDot: React.FC<{ classification: string }> = ({ classification }) => (
  <span
    className="inline-block w-2 h-2 rounded-full flex-shrink-0"
    style={{ backgroundColor: DOT_COLORS[classification] || '#9ca3af' }}
  />
);

const Pill: React.FC<{ value: boolean | string; colorYes?: string; colorNo?: string }> = ({
  value,
  colorYes = '#ef4444',
  colorNo = '#10b981',
}) => {
  const isYes = value === true || value === 'Yes';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{
        backgroundColor: isYes ? `${colorYes}18` : `${colorNo}18`,
        color: isYes ? colorYes : colorNo,
        border: `1px solid ${isYes ? colorYes : colorNo}30`,
      }}
    >
      {isYes ? 'Yes' : 'No'}
    </span>
  );
};

const formatValue = (v: AdmetValue): string => {
  if (v.value === undefined || v.value === null) return '—';
  if (typeof v.value === 'boolean') return v.value ? 'Yes' : 'No';
  const num = typeof v.value === 'number' ? v.value : parseFloat(String(v.value));
  if (isNaN(num)) return String(v.value);
  return num % 1 === 0 ? String(num) : num.toFixed(2);
};

const LABEL_MAP: Record<string, string> = {
  caco2_permeability: 'Solubility (ESOL)',
  pampa_permeability: 'PAMPA Permeability',
  hia: 'GI Absorption',
  pgp_substrate: 'Lipinski Violations',
  oral_bioavailability: 'Bioavailability Score',
  plasma_protein_binding: 'Plasma Protein Binding',
  bbb_permeability: 'BBB Penetration',
  vdss: 'Lipophilicity',
  fraction_unbound: 'Fraction Unbound',
  metabolic_stability: 'Metabolic Stability',
  total_clearance: 'Total Clearance',
  renal_oct2_substrate: 'Veber Violations',
  herg_inhibition: 'PAINS Alerts',
  ames_mutagenicity: 'AMES Mutagenicity',
  hepatotoxicity_dili: 'Brenk Alerts',
  skin_sensitisation: 'Drug-likeness',
  carcinogenicity: 'Carcinogenicity',
};

// ── Section Component ────────────────────────────────────────────────────────

const SectionCard: React.FC<{
  title: string;
  icon: React.ReactNode;
  items: Record<string, AdmetValue>;
}> = ({ title, icon, items }) => {
  const entries = Object.entries(items);
  if (entries.length === 0) return null;

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 border-b"
        style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}
      >
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
          {title}
        </span>
      </div>
      <div className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
        {entries.map(([key, val]) => (
          <div
            key={key}
            className="flex items-center justify-between px-3 py-1.5 gap-2"
          >
            <div className="flex items-center gap-2 min-w-0">
              <TrafficDot classification={val.classification} />
              <span className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                {LABEL_MAP[key] || key.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {typeof val.value === 'boolean' ? (
                <Pill value={val.value} />
              ) : (
                <span className="text-xs font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                  {formatValue(val)}
                  {val.unit && (
                    <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>
                      {val.unit}
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── CYP Section ──────────────────────────────────────────────────────────────

const CYPSection: React.FC<{
  cypData: Record<string, CypIsoform>;
  stability: AdmetValue;
}> = ({ cypData, stability }) => (
  <div
    className="rounded-lg border overflow-hidden"
    style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--card-bg)' }}
  >
    <div
      className="flex items-center gap-2 px-3 py-2 border-b"
      style={{ borderColor: 'var(--card-border)', backgroundColor: 'var(--hover-bg)' }}
    >
      <Activity size={13} style={{ color: '#8b5cf6' }} />
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-primary)' }}>
        Metabolism
      </span>
    </div>
    <div className="divide-y" style={{ borderColor: 'var(--card-border)' }}>
      {Object.entries(cypData).map(([isoform, data]) => (
        <div key={isoform} className="flex items-center justify-between px-3 py-1.5">
          <div className="flex items-center gap-2">
            <TrafficDot classification={data.classification} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {isoform} Inhibitor
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono" style={{ color: 'var(--text-primary)' }}>
              {(data.inhibition_probability * 100).toFixed(0)}%
            </span>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: data.classification === 'green' ? '#10b98118' : data.classification === 'amber' ? '#f59e0b18' : '#ef444418',
                color: DOT_COLORS[data.classification],
              }}
            >
              {data.risk}
            </span>
          </div>
        </div>
      ))}
      {/* Metabolic stability */}
      <div className="flex items-center justify-between px-3 py-1.5">
        <div className="flex items-center gap-2">
          <TrafficDot classification={stability.classification} />
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Metabolic Stability
          </span>
        </div>
        <span className="text-xs font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
          {formatValue(stability)}
          {stability.unit && (
            <span className="text-[10px] ml-1" style={{ color: 'var(--text-muted)' }}>{stability.unit}</span>
          )}
        </span>
      </div>
    </div>
  </div>
);

// ── Alert Banner ─────────────────────────────────────────────────────────────

const AlertBanner: React.FC<{ redCount: number; amberCount: number }> = ({ redCount, amberCount }) => {
  if (redCount === 0 && amberCount === 0) return null;
  const totalFlags = redCount + amberCount;
  const severity = redCount > 0 ? 'red' : 'amber';

  return (
    <div
      className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium"
      style={{
        backgroundColor: severity === 'red' ? '#fef2f2' : '#fffbeb',
        border: `1px solid ${severity === 'red' ? '#fecaca' : '#fde68a'}`,
        color: severity === 'red' ? '#991b1b' : '#92400e',
      }}
    >
      <AlertTriangle size={14} />
      <span>
        {totalFlags} ADMET flag{totalFlags !== 1 ? 's' : ''} detected
        {redCount > 0 && <span> ({redCount} critical)</span>}
      </span>
    </div>
  );
};

// ── Structural Alerts ────────────────────────────────────────────────────────

const StructuralAlerts: React.FC<{ toxResult: ToxResult }> = ({ toxResult }) => {
  if (toxResult.total_alerts === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534' }}>
        <Shield size={13} />
        <span>No structural alerts detected (PAINS/BRENK/toxicophore)</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--card-border)' }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: 'var(--card-border)', backgroundColor: '#fef2f2' }}>
        <Shield size={13} style={{ color: '#ef4444' }} />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#991b1b' }}>
          Structural Alerts ({toxResult.total_alerts})
        </span>
      </div>
      <div className="p-2 space-y-1">
        {toxResult.alerts.map((alert, i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-1 rounded" style={{ backgroundColor: '#fef2f2' }}>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: '#ef444418', color: '#ef4444', border: '1px solid #ef444430' }}>
              {alert.alert_type}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-primary)' }}>{alert.alert_name}</span>
            {alert.description && (
              <span className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{alert.description}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const ADMETTab: React.FC<ADMETTabProps> = ({ smiles }) => {
  const [admetData, setAdmetData] = useState<AdmetResult | null>(null);
  const [toxData, setToxData] = useState<ToxResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetched, setFetched] = useState(false);

  const fetchData = useCallback(async () => {
    if (!smiles) return;
    setLoading(true);
    setError(null);
    try {
      const [admetRes, toxRes] = await Promise.all([
        apiClient.post('/v2/admet/predict', { smiles }),
        apiClient.post('/v2/toxicophores/scan', { smiles }),
      ]);
      // Transform flat backend response → nested AdmetResult
      const d = admetRes.data;
      const cls = (good: boolean): 'green' | 'amber' | 'red' => good ? 'green' : 'red';
      const clsAmber = (val: number, lo: number, hi: number): 'green' | 'amber' | 'red' =>
        val >= lo && val <= hi ? 'green' : 'amber';

      const admet: AdmetResult = {
        smiles: d.canonical_smiles || d.smiles || smiles,
        descriptors: { mw: d.mw, logp: d.logp, tpsa: d.tpsa, hbd: d.hbd, hba: d.hba, rotatable_bonds: d.rotatable_bonds, rings: d.ring_count, fsp3: d.fsp3, molar_refractivity: d.molar_refractivity },
        absorption: {
          ...(d.gi_absorption != null ? { hia: { value: d.gi_absorption === 'High', classification: cls(d.gi_absorption === 'High'), note: d.gi_absorption } } : {}),
          ...(d.bioavailability_score != null ? { oral_bioavailability: { value: d.bioavailability_score, classification: d.bioavailability_score >= 0.55 ? 'green' : d.bioavailability_score >= 0.3 ? 'amber' : 'red' } } : {}),
          ...(d.esol_logs != null ? { caco2_permeability: { value: d.esol_logs, classification: d.esol_class === 'Highly soluble' || d.esol_class === 'Soluble' ? 'green' : 'amber', note: d.esol_class } } : {}),
          ...(d.lipinski_violations != null ? { pgp_substrate: { value: d.lipinski_violations, classification: d.lipinski_violations === 0 ? 'green' : d.lipinski_violations <= 1 ? 'amber' : 'red', note: `${d.lipinski_violations} Lipinski violation(s)` } } : {}),
        },
        distribution: {
          ...(d.bbb_permeant != null ? { bbb_permeability: { value: d.bbb_permeant === 'Yes', classification: cls(d.bbb_permeant === 'Yes') } } : {}),
          ...(d.logp != null ? { vdss: { value: d.logp, classification: clsAmber(d.logp, -0.4, 5.6), unit: 'LogP' } } : {}),
        },
        metabolism: {
          cyp_inhibition: {},
          metabolic_stability: { classification: 'green', note: 'Not predicted' },
        },
        excretion: {
          ...(d.veber_violations != null ? { renal_oct2_substrate: { value: d.veber_violations, classification: d.veber_violations === 0 ? 'green' : 'red', note: `${d.veber_violations} Veber violation(s)` } } : {}),
        },
        toxicity: {
          ...(d.pains_alerts != null ? { herg_inhibition: { value: d.pains_alerts, classification: d.pains_alerts === 0 ? 'green' : 'red', note: `${d.pains_alerts} PAINS alert(s)` } } : {}),
          ...(d.brenk_alerts != null ? { hepatotoxicity_dili: { value: d.brenk_alerts, classification: d.brenk_alerts === 0 ? 'green' : 'amber', note: `${d.brenk_alerts} Brenk alert(s)` } } : {}),
          ...(d.druglike != null ? { skin_sensitisation: { value: d.druglike, classification: cls(d.druglike === true), note: d.druglike ? 'Drug-like' : 'Not drug-like' } } : {}),
        },
      };
      setAdmetData(admet);
      // Transform backend {pains,brenk,nih} → frontend {alerts[]}
      const raw = toxRes.data;
      const alerts: ToxAlert[] = [];
      for (const cat of ['pains', 'brenk', 'nih'] as const) {
        const group = raw?.[cat];
        if (group?.matches) {
          group.matches.forEach((desc: string) => {
            alerts.push({ alert_name: desc, alert_type: cat.toUpperCase() });
          });
        }
      }
      setToxData({
        smiles: raw?.smiles || smiles,
        total_alerts: raw?.total_alerts || alerts.length,
        overall_risk: alerts.length > 0 ? 'high' : 'low',
        alerts,
      });
      setFetched(true);
    } catch (err: any) {
      if (err.response) {
        // Hub is reachable — show actual error
        setError(err.response.data?.detail || err.message || 'ADMET prediction failed');
      } else {
        setError('Could not reach the ADMET hub');
      }
    } finally {
      setLoading(false);
    }
  }, [smiles]);

  // Auto-fetch on mount
  useEffect(() => {
    if (!fetched && smiles) {
      fetchData();
    }
  }, [smiles, fetched, fetchData]);

  // Reset when SMILES changes
  useEffect(() => {
    setFetched(false);
    setAdmetData(null);
    setToxData(null);
    setError(null);
  }, [smiles]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#0d9488' }} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Running ADMET prediction...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }}>
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle size={14} style={{ color: '#d97706' }} />
          <span className="text-sm font-medium" style={{ color: '#92400e' }}>Could not reach the ADMET service</span>
        </div>
        <p className="text-xs mb-3" style={{ color: '#a16207' }}>It may be restarting. Try again in a moment.</p>
        <button
          onClick={fetchData}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors"
          style={{ backgroundColor: '#0d9488', color: 'white' }}
        >
          <RotateCw size={12} /> Retry
        </button>
      </div>
    );
  }

  if (!fetched || !admetData) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Shield size={24} style={{ color: 'var(--text-muted)' }} />
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>ADMET Profiling</p>
        <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-muted)' }}>
          Predict absorption, distribution, metabolism, excretion and toxicity properties.
        </p>
        <button
          onClick={fetchData}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          style={{ backgroundColor: '#0d9488', color: 'white' }}
        >
          <Activity size={13} /> Analyse
        </button>
      </div>
    );
  }

  // Count flags
  const countByClassification = (classification: string): number => {
    let count = 0;
    const sections = [admetData.absorption, admetData.distribution, admetData.excretion, admetData.toxicity];
    for (const section of sections) {
      for (const val of Object.values(section)) {
        if ((val as AdmetValue).classification === classification) count++;
      }
    }
    // CYP
    for (const val of Object.values(admetData.metabolism.cyp_inhibition)) {
      if (val.classification === classification) count++;
    }
    if (admetData.metabolism.metabolic_stability.classification === classification) count++;
    return count;
  };

  const redCount = countByClassification('red');
  const amberCount = countByClassification('amber');

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          onClick={() => window.open(buildHubUrl('admet', { smiles }), '_blank')}
          title="Open ADMET Hub in a new tab with this compound"
          className="flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium transition-colors"
          style={{ color: '#0d9488', border: '1px solid rgba(13,148,136,0.3)', backgroundColor: 'transparent' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(13,148,136,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          <ExternalLink size={11} />
          Open in ADMET Hub
        </button>
      </div>
      <AlertBanner redCount={redCount} amberCount={amberCount} />

      <SectionCard
        title="Absorption"
        icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
        items={admetData.absorption}
      />

      <SectionCard
        title="Distribution"
        icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>}
        items={admetData.distribution}
      />

      <CYPSection
        cypData={admetData.metabolism.cyp_inhibition}
        stability={admetData.metabolism.metabolic_stability}
      />

      <SectionCard
        title="Excretion"
        icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M2 12h20"/></svg>}
        items={admetData.excretion}
      />

      <SectionCard
        title="Toxicity"
        icon={<AlertTriangle size={13} style={{ color: '#ef4444' }} />}
        items={admetData.toxicity}
      />

      {toxData && <StructuralAlerts toxResult={toxData} />}
    </div>
  );
};

export default ADMETTab;
